import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';

import { Rating } from '@codex/basics';
import { ClosableTag } from '@codex/organisms';

import { getCurrentActivePriceFilterLabel } from './utils/filterHelpers';
import { removeSingleValueFromFilter, resetAllFilters } from '../../reduck/listingPagePages.reduck';
import { getActiveListingPage } from '../../reduck/selectors';


const ActiveFiltersWrapper = styled.div`
  padding: 1rem 10px 10px;
  border-bottom: 1px solid #ccc;
  
  .closable-tag-container {
    padding: 4px 4px 3px 9px;
    border-radius: 24px;
    margin: 0 4px 5px 0;
    
    ${({ theme }) => css`
      background: ${theme.colors.message.hover};
      border: 1px solid ${theme.colors.link.default};
      color: ${theme.colors.shades[3]};
    `};
  }
  
  .closable-tag-icon {
    ${({ theme }) => css`
      color: ${theme.colors.link.default};
    `};
    
  }
`;

const ActiveFiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 1em;

  @media ${({ theme }) => theme.breakpoints.mobile || theme.breakpoints.tablet} {
    font-size: 18px;
    padding-bottom: 10px;
    margin-bottom: 12px;
    ${({ theme }) => css`
      border-bottom: 1px solid ${theme.colors.shades[5]};
    `};
  }
`;

const ActiveFiltersTitle = styled.span`
  font-weight: bold;
`;

const ActiveFiltersResetButton = styled.a`
  cursor: pointer;
  ${({ theme }) => css`
    color: ${theme.colors.message.default};
  `};

  @media ${({ theme }) => theme.breakpoints.mobile || theme.breakpoints.tablet} {
    display: none;
  }
`;


function ActiveFilters(
  { currentActiveMinPrice, currentActiveMaxPrice, displayResetButton, filtersFromSearchLabs, onActiveFiltersResetButtonClick,
    onCloseableTagCloseButtonClick, segmentFilter }) {

  function renderClosableTagForCustomerRatingFilter(ratingFilterItem) {
    return (
      <ClosableTag
        attrs={{
          container: { className: 'closable-tag-container' },
          closeIcon: { className: 'closable-tag-icon' }
        }}
        key={`rounded_score${ratingFilterItem.value}`}
        onCloseClick={(e) => {
          e.preventDefault();
          onCloseableTagCloseButtonClick(
            'rounded_score',
            ratingFilterItem.value,
            'Customer rating',
            String(Math.min(...ratingFilterItem.value.split(',').map((singleValue) => Number(singleValue))) / 2) +
              (ratingFilterItem.value !== '10' ? ' or more' : '')
          );
        }}
      >
        <Rating
          maxCount={5}
          ratingValue={Math.min(...ratingFilterItem.value.split(',').map((singleValue) => Number(singleValue)))}
        />
      </ClosableTag>
    );
  }

  function renderClosableTagForActiveFilter(filterTypeId, filterValueId, filterTypeLabel, filterValueLabel) {
    return (
      <ClosableTag
        attrs={{
          container: { className: 'closable-tag-container' },
          closeIcon: { className: 'closable-tag-icon' }
        }}
        key={`${filterTypeId}${filterValueId}`}
        onCloseClick={(e) => {
          e.preventDefault();
          onCloseableTagCloseButtonClick(filterTypeId, filterValueId, filterTypeLabel, filterValueLabel);
        }}
      >
        {(filterValueLabel.toLowerCase() === 'yes' || filterValueLabel.toLowerCase() === 'no') // if filter has label 'yes' or 'no', it has to also contain filter name label in ClosableTag
          ? `${filterTypeLabel}: ${filterValueLabel}`
          : filterValueLabel}
      </ClosableTag>
    );
  }

  function renderClosableTagForSearchLabsActiveFilter() {
    const searchLabsActiveFilterClosableTags = [];
    let segmentFilterSelected = false;

    filtersFromSearchLabs.forEach((filterItem) => {
      filterItem.values.forEach((valueItem) => {
        if (valueItem.selected) {
          if (filterItem.id === 'segment_id') {
            segmentFilterSelected = true;
          }
          searchLabsActiveFilterClosableTags.push(
            filterItem.id === 'rounded_score'
              ? renderClosableTagForCustomerRatingFilter(valueItem)
              : renderClosableTagForActiveFilter(
                filterItem.id,
                valueItem.value,
                filterItem.label === 'segment_id' ? 'Product Category' : filterItem.label, // we have to change label for segment_id
                valueItem.label ? valueItem.label : valueItem.value
              )
          );
        }
      });
    });

    if (segmentFilter && !segmentFilterSelected) {
      searchLabsActiveFilterClosableTags.push(
        renderClosableTagForActiveFilter('segment_id', segmentFilter.filterId, 'Category', segmentFilter.label)
      );
    }

    return searchLabsActiveFilterClosableTags.length > 0 ? searchLabsActiveFilterClosableTags : null;
  }


  return (
    <ActiveFiltersWrapper>
      <ActiveFiltersHeader>
        <ActiveFiltersTitle>Active filters:</ActiveFiltersTitle>
        {' '}
        {/* TODO: add to translations */}
        {displayResetButton && (
          <ActiveFiltersResetButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onActiveFiltersResetButtonClick();
            }}
          >
            Reset filters
            {/* TODO: add to translations */}
          </ActiveFiltersResetButton>
        )}
      </ActiveFiltersHeader>
      <div>
        {typeof currentActiveMinPrice !== 'undefined' &&
        renderClosableTagForActiveFilter(
          'price',
          getCurrentActivePriceFilterLabel(currentActiveMinPrice, currentActiveMaxPrice),
          'Price',
          getCurrentActivePriceFilterLabel(currentActiveMinPrice, currentActiveMaxPrice)
        )}
        {renderClosableTagForSearchLabsActiveFilter()}
      </div>
    </ActiveFiltersWrapper>
  );
}

ActiveFilters.displayName = 'ActiveFilters';

ActiveFilters.propTypes = {
  displayResetButton: PropTypes.bool,
  currentActiveMinPrice: PropTypes.string,
  currentActiveMaxPrice: PropTypes.string,
  filtersFromSearchLabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      count: PropTypes.string
    })).isRequired
  })),
  onCloseableTagCloseButtonClick: PropTypes.func,
  onActiveFiltersResetButtonClick: PropTypes.func,
  segmentFilter: PropTypes.shape({
    label: PropTypes.string,
    filterId: PropTypes.string
  })
};

ActiveFilters.defaultProps = {
  displayResetButton: false,
  currentActiveMinPrice: undefined,
  currentActiveMaxPrice: undefined,
  filtersFromSearchLabs: undefined,
  onCloseableTagCloseButtonClick: () => {},
  onActiveFiltersResetButtonClick: () => {},
  segmentFilter: null
};

const mapStateToProps = (state) => ({
  currentActiveMinPrice: typeof getActiveListingPage(state).pageAttributes.price_min !== 'undefined'
    ? getActiveListingPage(state).pageAttributes.price_min : undefined,
  currentActiveMaxPrice: typeof getActiveListingPage(state).pageAttributes.price_max !== 'undefined'
    ? getActiveListingPage(state).pageAttributes.price_max : undefined,
  segmentFilter: getActiveListingPage(state).pageAttributes.segmentFilter
    ? getActiveListingPage(state).pageAttributes.segmentFilter : null
});

const mapDispatchToProps = (dispatch) => ({
  onActiveFiltersResetButtonClick: () => {
    dispatch(resetAllFilters());
  },
  onCloseableTagCloseButtonClick: (filterTypeId, filterValueToBeRemovedId, filterTypeLabel, filterValueToBeRemovedLabel) => {
    dispatch(removeSingleValueFromFilter(filterTypeId, filterValueToBeRemovedId, filterTypeLabel, filterValueToBeRemovedLabel));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ActiveFilters);
